'use client';

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { CircularChartProps } from 'types/common';

const CircularChart: React.FC<CircularChartProps> = ({
  title,
  centerLabel,
  centerValue,
  data,
  className = '',
  onExpandClick,
}) => {
  return (
    <div
      className={`w-[468px] h-[500px] bg-white rounded-xl shadow-[1px_1px_10.100000381469727px_0px_rgba(0,0,0,0.15)] outline outline-1 outline-offset-[-1px] outline-black/10 inline-flex flex-col justify-start items-center ${className}`}
    >
      {/* Header */}
      <div className='self-stretch px-6 pt-6 pb-3 inline-flex justify-between items-center'>
        <div className="justify-start text-[var(--textBlack100)] text-base font-bold font-['Inter'] leading-normal">
          {title}
        </div>
        <div className='flex justify-end items-center gap-1'>
          <div
            className='w-4 h-4 relative overflow-hidden cursor-pointer hover:opacity-70 transition-opacity'
            onClick={onExpandClick}
          >
            <ArrowSquareOut size={16} className='text-[var(--textBlack100)]' />
          </div>
        </div>
      </div>

      {/* Chart Content */}
      <div className='self-stretch flex-1 flex flex-col justify-center items-center'>
        {/* Chart Container */}
        <div className='w-80 h-72 px-14 inline-flex justify-center items-center'>
          <div className='flex-1 flex justify-center items-center relative'>
            {/* Recharts Pie Chart */}
            <ResponsiveContainer width={240} height={240}>
              <PieChart>
                <Pie
                  data={data}
                  cx='50%'
                  cy='50%'
                  innerRadius={80}
                  outerRadius={120}
                  startAngle={90}
                  endAngle={-270}
                  dataKey='value'
                  stroke='white'
                  strokeWidth={2}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Center Text - Positioned absolutely over the chart */}
            <div className='absolute inset-0 flex flex-col justify-center items-center'>
              <div className="text-center justify-start text-neutral-500 text-base font-medium font-['Inter'] leading-snug">
                {centerLabel}
              </div>
              <div className="text-center justify-start text-neutral-950 text-3xl font-bold font-['Inter'] leading-9">
                {centerValue}
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className='w-96 px-6 pt-3 pb-6 flex flex-col justify-start items-start'>
          {data.map((item, index) => (
            <div key={index} className='self-stretch py-1.5 inline-flex justify-start items-center gap-3'>
              <div className='w-3 h-3 rounded-full' style={{ backgroundColor: item.color }} />
              <div className='w-[96px] justify-start text-neutral-700 text-sm font-normal leading-tight'>
                {item.name}
              </div>
              <div className='flex-1 text-right text-neutral-950 text-sm font-medium leading-tight'>
                {item.displayValue}
              </div>
              <div
                className={`flex-1 max-w-[60px] text-right text-neutral-950 text-sm font-medium leading-tight`}
                style={item.name === '' ? { visibility: 'hidden' } : {}}
              >
                {item?.percentage}%
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CircularChart;
